import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const HelpTitle = styled.h3`
  font-size: 1.2em;
  margin-bottom: 15px;
  border-bottom: 1px solid #e0e8f5;
  padding-bottom: 10px;
`;

const HelpSection = styled.div`
  margin-bottom: 15px;
`;

const HelpSectionTitle = styled.h4`
  font-size: 1em;
  color: rgba(44, 100, 218, 0.9);
  margin-bottom: 8px;
`;

const HelpText = styled.p`
  font-size: 0.9em;
  line-height: 1.4;
  margin-bottom: 10px;
  color: #33445e;
`;

const HelpPopup = ({
  expert,
  userCategories,
  displayStatus,
  meetingDateTime,
  isMettingScheduled,
  formatToDate,
  showHelp,
  popupPosition,
  isMobile,
  portalRef
}) => {
  useEffect(() => {
    if (showHelp) {
      const styleElement = document.createElement('style');
      styleElement.id = `hide-webkit-scrollbar-${expert.id}`;
      styleElement.innerHTML = `.help-popup-${expert.id}::-webkit-scrollbar { display: none !important; }`;
      document.head.appendChild(styleElement);

      return () => {
        const style = document.getElementById(`hide-webkit-scrollbar-${expert.id}`);
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
  }, [showHelp, expert.id]);

  useEffect(() => {
    const existingStyle = document.getElementById('status-badge-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'status-badge-styles';
      style.innerHTML = `
        .expert-status-badge.awaiting,
        .expert-status-badge.awaiting-meeting {
          color: #a2611c !important;
          border: 1px solid rgba(239, 155, 97, 0.3) !important;
          background-color: rgba(25, 41, 71, 0.156) !important;
          font-size: 0.8em !important;
          padding: 6px 8px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!showHelp || !portalRef.current) return null;

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const viewportHeight = window.innerHeight;
  const topOffset = popupPosition.top;
  const bottomPadding = 30;
  const maxAvailableHeight = viewportHeight - topOffset - bottomPadding;
  const maxPopupHeight = Math.min(viewportHeight * 0.8, maxAvailableHeight);

  console.log(`[HelpPopup ${expert.name}] Rendering with position:`, popupPosition);
  console.log(`[HelpPopup ${expert.name}] Max height calculated: ${maxPopupHeight}`);

  return ReactDOM.createPortal(
    <div
      className={`help-popup help-popup-${expert.id}`}
      style={{
        position: 'fixed',
        top: `${popupPosition.top}px`,
        right: `${popupPosition.right}px`,
        width: isMobile ? '85%' : '320px',
        background: 'linear-gradient(145deg, #f9fbff 0%, #ffffff 100%)',
        border: '1px solid #e0e8f5',
        borderRadius: '10px',
        padding: isMobile ? '15px' : '20px',
        boxShadow: '0 6px 20px rgba(39, 68, 131, 0.14)',
        zIndex: 999999999,
        color: '#192947',
        height: 'auto',
        maxHeight: `${maxPopupHeight}px`,
        overflowY: 'auto',
        pointerEvents: 'auto',
        marginBottom: '20px',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}
      onTouchMove={handleTouchMove}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <HelpTitle>Expert Card Guide</HelpTitle>

      <HelpSection>
        <HelpSectionTitle>Expert Profile Overview</HelpSectionTitle>
        <HelpText>
          This card represents an expert advisor ({expert.name}) who is available for consultation.
          The profile image and online status indicator (green dot when online) show advisor availability.
        </HelpText>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>Expertise & Qualifications</HelpSectionTitle>
        <HelpText>
          <strong>Expertise:</strong> {userCategories.join(', ')}
        </HelpText>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>Session Information</HelpSectionTitle>
        <HelpText>
          <strong>Advisory Journey Began:</strong> {formatToDate(expert.startTime)}<br />
          <strong>Current Status:</strong> {displayStatus}<br />
          {meetingDateTime ? (
            <>
              <strong>Meeting Scheduled:</strong> Yes<br />
              <strong>Meeting Date:</strong> {meetingDateTime.date}<br />
              <strong>Meeting Time:</strong> {meetingDateTime.startTime} to {meetingDateTime.endTime}
            </>
          ) : (
            <><strong>Meeting Scheduled:</strong> No</>
          )}
        </HelpText>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>Available Actions</HelpSectionTitle>
        <HelpText>
          <strong>{isMettingScheduled ? "Edit Meeting" : "Set Up Meeting"}:</strong> {isMettingScheduled ? "Modify your existing meeting time or details." : "Schedule your first meeting with this expert."}<br /><br />
          <strong>Go to Meeting:</strong> Join a virtual session with the expert where you can discuss your questions and receive advice.
        </HelpText>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>Mobile View</HelpSectionTitle>
        <HelpText>
          On smaller screens, the card layout adjusts automatically to fit your device. All features remain accessible, though you may need to scroll to see all information. Tap buttons to perform actions just like on desktop.
        </HelpText>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>Need More Help?</HelpSectionTitle>
        <HelpText>
          If you need assistance scheduling or joining a meeting, or have questions about your advisor, please contact support through the help section in your account settings.
        </HelpText>
      </HelpSection>
    </div>,
    portalRef.current
  );
};

export default HelpPopup;